/**
 * How PPA names are compared - a copy of corpers.online's `utilities/ppaNames.js`, so the PPA
 * autocomplete can match "Govt. Sec. Sch." to "Government Secondary School" as people type, from
 * the LGA's PPAs it already loaded: no request per keystroke, which matters on slow networks. The
 * backend checks again on submit. Keep the two in step - ppa-name.util.spec.ts and corpers.online's
 * tests/utilities/ppaNames.test.js check the same cases.
 */

/** Short forms people type, and the word they stand for. */
const ABBREVIATIONS: Record<string, string> = {
  sch: "school",
  schl: "school",
  sec: "secondary",
  st: "saint",
  sndry: "secondary",
  pry: "primary",
  pri: "primary",
  govt: "government",
  gov: "government",
  gvt: "government",
  comm: "community",
  comp: "comprehensive",
  coll: "college",
  hosp: "hospital",
  univ: "university",
  intl: "international",
  min: "ministry",
  dept: "department",
  ltd: "limited",
  co: "company",
  assoc: "association",
};

/** Words that don't tell two PPAs apart. */
const FILLER = new Set(["the", "of", "and", "plc"]);

/** The name's words, normalised - see nameKey(). */
export function nameTokens(name: string | null | undefined): string[] {
  return String(name ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // accents
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’`]/g, "") // King's -> kings
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => ABBREVIATIONS[word] ?? word)
    .filter((word) => !FILLER.has(word));
}

/**
 * The name as compared: lower case, no punctuation or accents, short forms spelled out, filler
 * words dropped, words in order ("General Hospital Ikeja" and "Ikeja General Hospital" are one).
 * Two names with the same key are the same name. Saved on each PPA (`name_key`) and on blocked names.
 */
export function nameKey(name: string | null | undefined): string {
  return nameTokens(name).sort().join(" ");
}

/**
 * How much a word tells two PPAs apart. Words most PPAs share ("government", "state") count for
 * little, what kind of place it is ("school", "hospital") for some, and everything else - usually
 * the place's own name or town - in full.
 */
const COMMON_WORDS = new Set([
  "government", "state", "federal", "community", "national", "local", "high", "model", "public",
  "limited", "company", "nigeria", "international", "new", "area",
]);
const KIND_WORDS = new Set([
  "school", "secondary", "primary", "nursery", "college", "university", "polytechnic", "hospital",
  "clinic", "health", "centre", "center", "ministry", "bank", "church", "mosque", "office", "agency",
  "commission", "council", "department", "academy", "institute", "comprehensive", "corporation",
  "association", "foundation", "services", "authority", "board",
]);
function wordWeight(word: string): number {
  if (COMMON_WORDS.has(word)) return 0.3;
  if (KIND_WORDS.has(word)) return 0.6;
  return 1;
}

function bigrams(text: string): Map<string, number> {
  const padded = ` ${text} `;
  const grams = new Map();
  for (let i = 0; i < padded.length - 1; i++) {
    const gram = padded.slice(i, i + 2);
    grams.set(gram, (grams.get(gram) ?? 0) + 1);
  }
  return grams;
}

/** Dice coefficient of the two words' letter pairs. */
function bigramSimilarity(a: string, b: string): number {
  const ga = bigrams(a);
  const gb = bigrams(b);
  let shared = 0;
  for (const [gram, count] of ga) {
    shared += Math.min(count, gb.get(gram) ?? 0);
  }
  return (2 * shared) / (a.length + 1 + b.length + 1);
}

/** The same word, or a typo of it ("grammer" for "grammar") - never a number for another. */
function wordsMatch(a: string, b: string): boolean {
  if (a === b) return true;
  if (/\d/.test(a) || /\d/.test(b) || a.length < 4 || b.length < 4) return false;
  return bigramSimilarity(a, b) >= 0.7;
}

/**
 * How alike two names are, 0 to 1 (1: the same key). Words are matched one to one, allowing typos,
 * and weighed by how much they tell PPAs apart (wordWeight), so "Government Secondary School Ikeja"
 * and "... Yaba" differ by the word that matters. A name whose own words all appear in the other
 * ("Kings College" in "Kings College Lagos") is close. Different numbers ("... School 1" and
 * "... School 2") are different places.
 */
export function nameSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const ta = nameTokens(a);
  const tb = nameTokens(b);
  if (!ta.length || !tb.length) return 0;
  if (nameKey(a) === nameKey(b)) return 1;

  const numbers = (tokens: string[]) => tokens.filter((word) => /\d/.test(word)).sort().join(" ");
  if (numbers(ta) && numbers(tb) && numbers(ta) !== numbers(tb)) return 0;

  const unmatched = [...tb];
  let matchedWeight = 0;
  let exact = true;
  const matchedInA: string[] = [];
  for (const word of ta) {
    const index = unmatched.findIndex((other) => wordsMatch(word, other));
    if (index === -1) continue;
    if (unmatched[index] !== word) exact = false;
    matchedWeight += wordWeight(word);
    matchedInA.push(word);
    unmatched.splice(index, 1);
  }
  const total = (tokens: string[]) => tokens.reduce((sum, word) => sum + wordWeight(word), 0);
  const dice = (2 * matchedWeight) / (total(ta) + total(tb));

  // All of the shorter name's own words (not just "school") are in the longer one.
  const [shorter] = ta.length <= tb.length ? [ta] : [tb];
  const distinctive = shorter.filter((word) => wordWeight(word) === 1);
  const allMatched = matchedInA.length === Math.min(ta.length, tb.length);
  const contained = allMatched && distinctive.length > 0 ? 0.9 : 0;

  const score = Math.max(dice, contained);
  return exact ? score : Math.min(score, 0.95); // a typo is never quite "the same name"
}

/** At or above this, two names are offered as possibly the same PPA. */
export const SIMILAR_NAME_THRESHOLD = 0.75

/** The candidates whose name is like `name`, most alike first. */
export function rankSimilarNames<T>(
  name: string,
  candidates: T[],
  nameOf: (item: T) => string,
  { threshold = SIMILAR_NAME_THRESHOLD, limit = 5 } = {},
): Array<{ item: T; score: number }> {
  return candidates
    .map((item) => ({ item, score: nameSimilarity(name, nameOf(item)) }))
    .filter(({ score }) => score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}

/**
 * The PPAs to suggest for what's typed, from a list already loaded: names containing it first (as
 * they type), then names like it - a typo, a short form, the words in another order.
 */
export function suggestPpas<T extends { name?: string | null }>(ppas: T[], typed: string): T[] {
  const text = typed.trim().toLowerCase()
  if (!text) return ppas
  const containing = ppas.filter((ppa) => ppa.name?.toLowerCase().includes(text))
  const rest = ppas.filter((ppa) => !containing.includes(ppa))
  const like = rankSimilarNames(typed, rest, (ppa) => ppa.name ?? '', { threshold: 0.6 }).map(({ item }) => item)
  return [...containing, ...like]
}
